import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import apiClient from '../api/client'
import type { JobState } from '../api/types'

interface RunResult {
  id: string
  research_question_id: string
  collection_name: string
  status: JobState
  final_response: string | null
  updated_at: string
}

interface QuestionMap {
  [id: string]: string
}

function GlobalSearchPage(): JSX.Element {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<RunResult[]>([])
  const [questions, setQuestions] = useState<QuestionMap>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasSearched, setHasSearched] = useState(false)

  const doSearch = async (q: string) => {
    const trimmed = q.trim()
    if (trimmed.length < 2) {
      setError('Enter at least 2 characters')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const [searchRes, qRes] = await Promise.all([
        apiClient.get<RunResult[]>(`/research/search?q=${encodeURIComponent(trimmed)}`),
        apiClient.get<{ id: string; question_text: string }[]>('/research/questions'),
      ])
      setResults(searchRes.data as unknown as RunResult[])
      const qMap: QuestionMap = {}
      const qData = qRes.data as unknown as { id: string; question_text: string }[]
      qData.forEach((qq) => { qMap[qq.id] = qq.question_text })
      setQuestions(qMap)
      setHasSearched(true)
    } catch (err: unknown) {
      const maybeAxios = err as { response?: { data?: { detail?: string } } }
      setError(maybeAxios?.response?.data?.detail ?? 'Search failed')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const t = setTimeout(() => {
      if (query.trim().length >= 2) doSearch(query)
    }, 500)
    return () => clearTimeout(t)
  }, [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    doSearch(query)
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Search All Researches</h1>
      </div>
      <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 8, marginBottom: 16, maxWidth: 600 }}>
        <input type="text" placeholder="Keyword in question, results, topics, collection..." value={query} onChange={(e) => setQuery(e.target.value)} className="form-input" style={{ flex: 1 }} />
        <button type="submit" className="button-primary" disabled={loading}>{loading ? 'Searching...' : 'Search'}</button>
      </form>
      {error && <p style={{ color: '#c00', fontSize: 14 }}>{error}</p>}
      {!hasSearched && !loading && <p style={{ fontSize: 14, color: '#64748b' }}>Enter a keyword to search across question titles, results, and topics.</p>}
      {hasSearched && (
        <div className="detail-card detail-card-wide">
          <h2 className="detail-card-title">Results — {results.length}</h2>
          {results.length === 0 ? (
            <p style={{ fontSize: 14, color: '#666' }}>No results for “{query}”.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Question</th>
                  <th>Collection</th>
                  <th>Status</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => (
                  <tr key={r.id}>
                    <td style={{ maxWidth: 400 }}>
                      <Link to={`/runs/${r.id}`} style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: 500 }}>
                        {(questions[r.research_question_id] ?? r.research_question_id.slice(0, 8)).slice(0, 120)}
                      </Link>
                      {r.final_response && <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, whiteSpace: 'normal', wordBreak: 'break-word' }}>{r.final_response.slice(0, 120)}</div>}
                    </td>
                    <td style={{ fontSize: 12 }}>{r.collection_name}</td>
                    <td><span className={`badge badge--${r.status}`}>{r.status.replace(/_/g, ' ')}</span></td>
                    <td style={{ fontSize: 12, color: '#64748b' }}>{new Date(r.updated_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  )
}

export default GlobalSearchPage
